import React, { useState, useMemo } from'react';
import { useApiQuery } from'../../hooks/useApi';
import { useQueryClient } from'@tanstack/react-query';
import api from'../../api/client';
import { Plus, Trash2, ChevronDown, ChevronRight, DollarSign, Loader2 } from'lucide-react';

export default function ModelPricingEditor() {
 const queryClient = useQueryClient();
 const [expandedModel, setExpandedModel] = useState(null);
 const [showAddForm, setShowAddForm] = useState(null); // modelId or'__new__'
 const [saving, setSaving] = useState(false);

 const { data: allPricing, isLoading } = useApiQuery(
'all-pricing',
 () => api.getAllPricing()
 );

 // Group by model
 const grouped = useMemo(() => {
 const map = {};
 for (const p of allPricing || []) {
 if (!p || !p.modelId) continue;
 if (!map[p.modelId]) map[p.modelId] = [];
 map[p.modelId].push(p);
 }
 // Sort tiers by effectiveFrom descending. Guard against missing
 // dates so a single bad row doesn't blow up the entire editor.
 for (const key of Object.keys(map)) {
 map[key].sort((a, b) => (b.effectiveFrom ||'').localeCompare(a.effectiveFrom ||''));
 }
 return map;
 }, [allPricing]);

 const modelIds = Object.keys(grouped).sort();

 const [form, setForm] = useState({
 modelId:'',
 provider:'azure-openai',
 effectiveFrom: new Date().toISOString().slice(0, 10),
 promptTokensPer1K:'',
 completionTokensPer1K:'',
 cachedInputPer1K:'',
 currency:'USD',
 });

 const resetForm = () => setForm({
 modelId:'',
 provider:'azure-openai',
 effectiveFrom: new Date().toISOString().slice(0, 10),
 promptTokensPer1K:'',
 completionTokensPer1K:'',
 cachedInputPer1K:'',
 currency:'USD',
 });

 const handleSubmit = async (e) => {
 e.preventDefault();
 setSaving(true);
 try {
 const data = {
 modelId: showAddForm ==='__new__' ? form.modelId : showAddForm,
 provider: form.provider,
 effectiveFrom: form.effectiveFrom,
 promptTokensPer1K: parseFloat(form.promptTokensPer1K),
 completionTokensPer1K: parseFloat(form.completionTokensPer1K),
 cachedInputPer1K: form.cachedInputPer1K ? parseFloat(form.cachedInputPer1K) : 0,
 currency: form.currency,
 };
 await api.createPricing(data);
 await queryClient.invalidateQueries({ queryKey: ['all-pricing'], refetchType: 'all' });
 setShowAddForm(null);
 resetForm();
 } catch (err) {
 alert('Failed to save pricing:' + (err.message ||'Unknown error'));
 } finally {
 setSaving(false);
 }
 };

 const handleDelete = async (pricingId, modelId) => {
 if (!window.confirm('Delete this pricing tier?')) return;
 try {
 await api.deletePricing(pricingId, modelId);
 } catch (err) {
 // A 404 means the tier is already gone — treat delete as idempotent
 // and fall through to the refetch so the stale row disappears.
 if (err.status !== 404) {
 alert('Failed to delete:' + (err.message ||'Unknown error'));
 return;
 }
 }
 // Force an immediate refetch (not just a background invalidation) so the
 // list reflects the current DB state right away.	await queryClient.invalidateQueries({ queryKey: ['all-pricing'], refetchType: 'all' });
 };

 if (isLoading) {
 return (
 <div className="flex items-center justify-center py-12">
 <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
 </div>
 );
 }

 return (
 <div className="space-y-4">
 {/* Header actions */}
 <div className="flex items-center justify-between">
 <h2 className="text-lg font-semibold flex items-center gap-2">
 <DollarSign className="w-5 h-5 text-emerald-500" />
 Model Pricing Tiers
 </h2>        <div className="flex gap-2">
          <button
            onClick={() => { setShowAddForm('__new__'); resetForm(); }}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-600 text-white
                       hover:bg-brand-700 transition flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Add Model
          </button>
        </div>
 </div>

 {/* Model List */}
 {modelIds.length === 0 && !showAddForm && (
 <div className="text-center py-8 text-gray-400">          <p>No pricing configured. Click "Add Model" to add a model.</p>
 </div>
 )}

 {modelIds.map((modelId) => (
 <div key={modelId} className="border border-gray-200 rounded-lg overflow-hidden">
 {/* Model header */}
 <button
 onClick={() => setExpandedModel(expandedModel === modelId ? null : modelId)}
 className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 
 hover:bg-gray-100 transition text-left"
 >
 <div className="flex items-center gap-3">
 {expandedModel === modelId
 ? <ChevronDown className="w-4 h-4 text-gray-400" />
 : <ChevronRight className="w-4 h-4 text-gray-400" />}
 <span className="font-mono font-medium text-sm">{modelId}</span>
 <span className="text-xs text-gray-400">
 {grouped[modelId][0].provider} · {grouped[modelId].length} tier{grouped[modelId].length !== 1 ?'s' :''}
 </span>
 </div>
 <div className="text-xs text-gray-500 font-mono">
 ${grouped[modelId][0].promptTokensPer1K}/1K prompt · ${grouped[modelId][0].completionTokensPer1K}/1K completion
 </div>
 </button>

 {/* Expanded tiers */}
 {expandedModel === modelId && (
 <div className="divide-y divide-gray-100">
 {grouped[modelId].map((tier) => (                <div key={tier.id} className="px-4 py-3 flex items-center justify-between">
                 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 flex-1 text-sm">
 <div>
 <span className="text-xs text-gray-400">Effective From</span>
 <div className="font-mono">{tier.effectiveFrom}</div>
 </div>
 <div>
 <span className="text-xs text-gray-400">Effective To</span>
 <div className="font-mono">{tier.effectiveTo ||'— (current)'}</div>
 </div>
 <div>
 <span className="text-xs text-gray-400">Prompt/1K</span>
 <div className="font-mono text-emerald-600">${tier.promptTokensPer1K}</div>
 </div>
 <div>
 <span className="text-xs text-gray-400">Completion/1K</span>
 <div className="font-mono text-emerald-600">${tier.completionTokensPer1K}</div>
 </div>
 <div>
 <span className="text-xs text-gray-400">Cached Input/1K</span>
 <div className="font-mono">${tier.cachedInputPer1K || 0}</div>
 </div>
 </div>
 <button
 onClick={() => handleDelete(tier.id, modelId)}
 className="p-1.5 text-gray-400 hover:text-red-500 transition ml-3"
 title="Delete tier"
 >
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 ))}
 {/* Add tier button for this model */}
 <div className="px-4 py-2">
 <button
 onClick={() => {
 // Reset numeric fields so a previous attempt doesn't
 // leak into the new form, but preserve the modelId
 // and provider derived from the row being expanded.
 resetForm();
 setForm((f) => ({ ...f, modelId, provider: grouped[modelId][0].provider }));
 setShowAddForm(modelId);
 }}
 className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1"
 >
 <Plus className="w-3.5 h-3.5" /> Add pricing tier
 </button>
 </div>
 </div>
 )}
 </div>
 ))}

 {/* Add Form */}
 {showAddForm && (
 <div className="border border-brand-200 rounded-lg p-4 bg-brand-50/50">
 <h3 className="text-sm font-semibold mb-3">
 {showAddForm ==='__new__' ?'Add New Model Pricing' :`Add Pricing Tier for ${showAddForm}`}
 </h3>
 <form onSubmit={handleSubmit} className="grid grid-cols-2 md:grid-cols-4 gap-3">
 {showAddForm ==='__new__' && (
 <div>
 <label className="text-xs text-gray-500 mb-1 block">Model ID</label>
 <input
 type="text"
 required
 value={form.modelId}
 onChange={(e) => setForm({ ...form, modelId: e.target.value })}
 placeholder="e.g. gpt-4o"
 className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
 bg-white outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>
 )}
 {showAddForm ==='__new__' && (
 <div>
 <label className="text-xs text-gray-500 mb-1 block">Provider</label>
 <select
 value={form.provider}
 onChange={(e) => setForm({ ...form, provider: e.target.value })}
 className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
 bg-white outline-none focus:ring-2 focus:ring-brand-500"
 >
 <option value="azure-openai">Azure OpenAI</option>
 <option value="openai">OpenAI</option>
 <option value="anthropic">Anthropic</option>
 <option value="google">Google</option>
 <option value="mistral">Mistral</option>
 <option value="custom">Custom</option>
 </select>
 </div>
 )}
 <div>
 <label className="text-xs text-gray-500 mb-1 block">Effective From</label>
 <input
 type="date"
 required
 value={form.effectiveFrom}
 onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
 className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
 bg-white outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>
 <div>            <label className="text-xs text-gray-500 mb-1 block">Prompt $/1K tokens</label>
            <input
              type="number"
              step="any"
              min="0"
              required
              value={form.promptTokensPer1K}
 onChange={(e) => setForm({ ...form, promptTokensPer1K: e.target.value })}
 placeholder="0.0025"
 className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
 bg-white outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>
 <div>            <label className="text-xs text-gray-500 mb-1 block">Completion $/1K tokens</label>
            <input
              type="number"
              step="any"
              min="0"
              required
              value={form.completionTokensPer1K}
 onChange={(e) => setForm({ ...form, completionTokensPer1K: e.target.value })}
 placeholder="0.01"
 className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
 bg-white outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>
 <div>            <label className="text-xs text-gray-500 mb-1 block">Cached Input $/1K</label>
            <input
              type="number"
              step="any"
              min="0"
              value={form.cachedInputPer1K}
 onChange={(e) => setForm({ ...form, cachedInputPer1K: e.target.value })}
 placeholder="0.00125"
 className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
 bg-white outline-none focus:ring-2 focus:ring-brand-500"
 />
 </div>
 <div className="col-span-full flex items-center gap-3 mt-1">
 <button
 type="submit"
 disabled={saving}
 className="px-5 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg
 hover:bg-brand-700 transition disabled:opacity-50 flex items-center gap-2"
 >
 {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
 Save Pricing Tier
 </button>
 <button
 type="button"
 onClick={() => { setShowAddForm(null); resetForm(); }}
 className="px-5 py-2 text-sm text-gray-600 hover:text-gray-800
 transition"
 >
 Cancel
 </button>
 </div>
 </form>
 </div>
 )}

 {/* Help text */}
 <div className="text-xs text-gray-400 mt-4 space-y-1">
 <p>
 <strong>Time-based pricing:</strong> Add multiple tiers per model with different effective dates. 
 The system automatically uses the correct pricing for each API call based on the call's timestamp.
 </p>
 <p>
 When you add a new tier, the previous tier's"Effective To" date is automatically set to the day 
 before the new tier starts.
 </p>
 </div>
 </div>
 );
}
